import { useCallback, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AssistantDock } from '../features/assistant/AssistantDock';
import { useAuth } from '../features/auth/useAuth';
import { TABLET_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { BrandSwitcher } from './BrandSwitcher';
import { Logo } from './Logo';
import { CommandPalette } from './commandPalette/CommandPalette';
import { useCommandPaletteShortcut } from './commandPalette/useCommandPaletteShortcut';
import { MobileTabBar } from './MobileTabBar';
import { MoreSheet } from './MoreSheet';
import { LockedBrandBanner } from './plan/LockedBrandBanner';
import { PastDueBanner } from './plan/PastDueBanner';
import { PlanProvider } from './plan/PlanProvider';
import { TrialBanner } from './plan/TrialBanner';
import { usePlan } from './plan/usePlan';
import { QuotaProvider } from './quota/QuotaProvider';
import { QuotaCounter } from './QuotaCounter';
import { GLYPH_SEARCH, linksForPlan, NAV_GROUPS } from './shellNav';
import { ThemeToggle } from './ThemeToggle';
import { UndoToastProvider } from './UndoToastProvider';

/**
 * App shell: sidebar navigation, header with the always-visible quota counter,
 * the Ctrl K command palette, the undo-toast host, and the routed screen.
 *
 * `QuotaProvider` sits above both the header and the `Outlet` on purpose: the
 * counter and the screens that spend a generation read the same number, so a
 * submit on `/generation` moves the figure in the header without a reload.
 * `PlanProvider` sits beside it for the same reason: one reading of the
 * account's plan for the settings card and every gate.
 *
 * One account may hold several brands; `BrandSwitcher` at the top of the
 * sidebar picks the active one, and every screen below is scoped to it.
 * Still single-user: no team invites.
 *
 * Three tiers (`../../claude files/MOBILE_RESPONSIVE_PLAN.md`), drawn by CSS:
 * desktop is the sidebar; a tablet folds it into a 64px icon rail (each link
 * gets a `title` there, since its label is visually hidden); a phone turns it
 * into a one-row top bar (wordmark, brand, quota, search) and moves the links
 * to `MobileTabBar` at the bottom, with the rest in `MoreSheet`. The phone bar
 * carries its own `QuotaCounter`; the header's is hidden there, so the count
 * is always on screen exactly once.
 */
export function AppLayout() {
  return (
    <UndoToastProvider>
      <QuotaProvider>
        <PlanProvider>
          <AppShell />
        </PlanProvider>
      </QuotaProvider>
    </UndoToastProvider>
  );
}

/** The shell itself, inside the providers so it can read the plan. */
function AppShell() {
  const { user, signOut } = useAuth();
  const { can } = usePlan();
  const location = useLocation();
  const isTablet = useMediaQuery(TABLET_QUERY);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  // The sheet is open for the location it was opened on: any navigation (a
  // link in it, the back button, a palette command) closes it without an effect.
  const [moreOpenAt, setMoreOpenAt] = useState<string | null>(null);
  const isMoreOpen = moreOpenAt !== null && moreOpenAt === location.key;
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const togglePalette = useCallback(() => setIsPaletteOpen((open) => !open), []);
  const closePalette = useCallback(() => setIsPaletteOpen(false), []);
  const openPalette = useCallback(() => setIsPaletteOpen(true), []);

  useCommandPaletteShortcut(togglePalette);

  function dismissMore() {
    setMoreOpenAt(null);
    moreButtonRef.current?.focus();
  }

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar">
        <div className="app-shell__brand">
          <Logo />
        </div>
        <BrandSwitcher />
        {/* Phone top bar only: the count and the way into the palette. */}
        <div className="app-shell__bar">
          <QuotaCounter />
          <button
            className="app-shell__search"
            type="button"
            aria-label="Search commands"
            onClick={openPalette}
          >
            <span aria-hidden="true">{GLYPH_SEARCH}</span>
          </button>
        </div>
        <nav className="nav" aria-label="Main">
          {NAV_GROUPS.map((group) => (
            <div className="nav__group" key={group.label}>
              <p className="nav__group-label">{group.label}</p>
              {linksForPlan(group.items, can).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={isTablet ? item.label : undefined}
                  className={({ isActive }) =>
                    isActive ? 'nav__link nav__link--active' : 'nav__link'
                  }
                >
                  <span className="nav__icon" aria-hidden="true">
                    {String.fromCodePoint(item.icon)}
                  </span>
                  <span className="nav__label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="app-shell__foot">
          {/* One quiet line: the shortcut, and the row opens the palette.
                  On the tablet rail it is a search icon. */}
          <button
            className="palette-hint"
            type="button"
            title={isTablet ? 'Search commands' : undefined}
            onClick={openPalette}
          >
            <span className="palette-hint__icon" aria-hidden="true">
              {GLYPH_SEARCH}
            </span>
            <span className="palette-hint__keys">
              <kbd className="kbd">Ctrl</kbd>
              <kbd className="kbd">K</kbd>
            </span>
            <span className="palette-hint__text">commands</span>
          </button>
        </div>
      </aside>

      <div className="app-shell__main">
        <header className="app-shell__header">
          {/* Quota counter slot — permanently visible on every screen. */}
          <QuotaCounter />
          <div className="app-shell__account">
            <ThemeToggle />
            {/* Settings left the sidebar; the address and the link both open it. */}
            <Link
              className="app-shell__email"
              to="/settings"
              title={isTablet ? user?.email : undefined}
            >
              {user?.email}
            </Link>
            <Link className="button button--ghost" to="/settings">
              Settings
            </Link>
            <button className="button button--ghost" type="button" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </header>

        <LockedBrandBanner />
        <PastDueBanner />
        <TrialBanner />

        <div className="app-shell__content">
          <Outlet />
        </div>
      </div>

      <MobileTabBar
        isMoreOpen={isMoreOpen}
        onOpenMore={() => setMoreOpenAt(location.key)}
        moreButtonRef={moreButtonRef}
      />

      {isMoreOpen ? (
        <MoreSheet
          email={user?.email ?? null}
          onDismiss={dismissMore}
          onNavigate={() => setMoreOpenAt(null)}
          onOpenPalette={() => {
            setMoreOpenAt(null);
            setIsPaletteOpen(true);
          }}
          onSignOut={() => void signOut()}
        />
      ) : null}

      {isPaletteOpen ? <CommandPalette onDismiss={closePalette} /> : null}

      {/* Once, above the routes, so it outlives navigation. */}
      <AssistantDock />
    </div>
  );
}
